import { DataTable } from '@/components/table/DataTable';
import { Box } from '@/components/ui/box';
import { Button } from '@/components/ui/button';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Text } from '@/components/ui/text';
import { MarkdownText } from '@/shared/MarkdownText';
import { UITools } from '@ditto/shared';
import { ColumnDef, getPaginationRowModel } from '@tanstack/react-table';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState, type FC } from 'react';
import { Link } from 'react-router';
import { generateRegulationLink } from './utils';

export type DeviceClassificationToolPart =
  UITools['fdaDeviceClassificationDbSearch'];
type Output = DeviceClassificationToolPart['output'];
export const DeviceClassificationToolMessage: FC<{
  input?: DeviceClassificationToolPart['input'];
  output?: Output;
}> = ({ input, output }) => {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const searchUrl = `https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpcd/classification.cfm?start_search=1&devicename=${input?.deviceName ?? ''}`;

  // Build markdown text for input parameters
  const markdownText = [
    input?.deviceName ? `- Device Name: ${input.deviceName}` : null,
    input?.productCode ? `- Product Code: ${input.productCode}` : null,
    input?.regulationNumber
      ? `- Regulation Number: ${input.regulationNumber}`
      : null,
  ]
    .filter(Boolean)
    .join('\n');

  const hasResults = output?.devices && output.devices.length > 0;

  return (
    <FlexRow alignItems="stretch" w="full" gap="4">
      <FlexCol gap="4">
        <FlexCol gap="2">
          <a href={searchUrl} target="_blank" rel="noopener noreferrer">
            <FlexRow gap="1">
              <Text variant="body" className="text-neutral-600">
                Search in
              </Text>
              <Text variant="body" className="text-neutral-600 underline">
                FDA Product Classification Database
              </Text>
              <Text variant="body" className="text-neutral-600">
                for:
              </Text>
            </FlexRow>
          </a>
          <MarkdownText>{markdownText}</MarkdownText>

          {/* Show metadata */}
          {output?.meta && (
            <FlexRow gap="2" className="text-sm text-neutral-500">
              <Text variant="body-sm">
                Total found: {output.meta.total?.toLocaleString() || 0}
              </Text>
              {output.meta.limit && (
                <Text variant="body-sm">
                  • Showing:{' '}
                  {Math.min(output.meta.limit, output.meta.total || 0)}
                </Text>
              )}
            </FlexRow>
          )}
        </FlexCol>

        {hasResults ? (
          <div className="overflow-y-auto max-w-[800px]">
            <DataTable
              columns={columns}
              data={output?.devices!}
              getRowCanExpand={() => true}
              renderSubComponent={device => (
                <AdditionalDetails device={device.original} />
              )}
              options={{
                getPaginationRowModel: getPaginationRowModel(),
                onPaginationChange: setPagination,
                state: {
                  pagination,
                },
              }}
            />
          </div>
        ) : (
          <FlexCol gap="2">
            <Text variant="body" className="text-neutral-600">
              No device classifications found.
            </Text>
          </FlexCol>
        )}
      </FlexCol>
    </FlexRow>
  );
};

const columns: ColumnDef<Output['devices'][number]>[] = [
  {
    id: 'expander',
    header: () => null,
    cell: ({ row }) => {
      return (
        <Button
          variant="ghost"
          size="sm"
          onClick={e => {
            e.stopPropagation();
            row.toggleExpanded();
          }}
          className="p-0 h-8 w-8"
        >
          {row.getIsExpanded() ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </Button>
      );
    },
    meta: {
      cellClassName: 'bg-white border-b border-neutral-200 min-w-[50px]',
      headerClassName:
        'bg-neutral-100 border-b border-neutral-200 min-w-[50px]',
    },
  },
  {
    accessorKey: 'deviceName',
    header: () => (
      <Text variant="body" color="primary">
        Device Name
      </Text>
    ),
    cell: ({ row }) => {
      const link = `https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfPCD/classification.cfm?id=${row.original.productCode}`;
      return (
        <Link to={link} target="_blank" rel="noopener noreferrer">
          <Text variant="body" className="text-neutral-600 hover:underline">
            {row.original.deviceName}
          </Text>
        </Link>
      );
    },
    meta: {
      cellClassName: 'bg-white border-b border-neutral-200 min-w-[200px]',
      headerClassName:
        'bg-neutral-100 border-b border-neutral-200 min-w-[200px]',
    },
  },
  {
    accessorKey: 'productCode',
    header: () => (
      <Text variant="body" color="primary">
        Product Code
      </Text>
    ),
    cell: ({ row }) => {
      return (
        <Text variant="body" className="text-neutral-600 font-mono">
          {row.original.productCode}
        </Text>
      );
    },
    meta: {
      cellClassName: 'bg-white border-b border-neutral-200 min-w-[125px]',
      headerClassName:
        'bg-neutral-100 border-b border-neutral-200 min-w-[125px]',
    },
  },
  {
    accessorKey: 'deviceClass',
    header: () => (
      <Text variant="body" color="primary">
        Device Class
      </Text>
    ),
    cell: ({ row }) => {
      return (
        <Text variant="body" className="text-neutral-600">
          {row.original.deviceClass}
        </Text>
      );
    },
    meta: {
      cellClassName: 'bg-white border-b border-neutral-200 min-w-[125px]',
      headerClassName:
        'bg-neutral-100 border-b border-neutral-200 min-w-[125px]',
    },
  },
  {
    accessorKey: 'regulationNumber',
    header: () => (
      <Text variant="body" color="primary">
        Regulation Number
      </Text>
    ),
    cell: ({ row }) => {
      const regulationNumber = row.original.regulationNumber;
      const regulationLink = generateRegulationLink(regulationNumber);

      if (!regulationNumber) {
        return (
          <Text variant="body" className="text-neutral-600 font-mono">
            —
          </Text>
        );
      }

      if (regulationLink) {
        return (
          <a
            href={regulationLink}
            target="_blank"
            rel="noopener noreferrer"
            className="text-neutral-600 underline font-mono"
          >
            {regulationNumber}
          </a>
        );
      }

      return (
        <Text variant="body" className="text-neutral-600 font-mono">
          {regulationNumber}
        </Text>
      );
    },
    meta: {
      cellClassName: 'bg-white border-b border-neutral-200 min-w-[150px]',
      headerClassName:
        'bg-neutral-100 border-b border-neutral-200 min-w-[150px]',
    },
  },
];

const AdditionalDetails: FC<{
  device: Output['devices'][number];
}> = ({ device }) => {
  return (
    <FlexCol gap="2">
      <Box className="grid grid-cols-2 gap-2">
        {device.medicalSpecialty && (
          <FlexRow gap="1">
            <Text variant="body-sm" className="text-neutral-600 font-bold">
              · Medical Specialty:
            </Text>
            <Text variant="body-sm" className="text-neutral-600">
              {device.medicalSpecialty}
            </Text>
          </FlexRow>
        )}
        {device.reviewPanel && (
          <FlexRow gap="1">
            <Text variant="body-sm" className="text-neutral-600 font-bold">
              · Review Panel:
            </Text>
            <Text variant="body-sm" className="text-neutral-600">
              {device.reviewPanel}
            </Text>
          </FlexRow>
        )}
        {device.submissionTypeId && (
          <FlexRow gap="1">
            <Text variant="body-sm" className="text-neutral-600 font-bold">
              · Submission Type ID:
            </Text>
            <Text variant="body-sm" className="text-neutral-600 font-mono">
              {device.submissionTypeId}
            </Text>
          </FlexRow>
        )}
        {device.productCode && (
          <FlexRow gap="1" className="col-span-2">
            <Text variant="body-sm" className="text-neutral-600 font-bold">
              · Product Life Cycle:
            </Text>
            <a
              href={`https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfTPLC/tplc.cfm?id=${device.productCode}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-neutral-600 underline font-mono text-sm"
            >
              View Product Life Cycle
            </a>
          </FlexRow>
        )}
        {device.definition && (
          <FlexCol gap="1" className="col-span-2">
            <Text variant="body-sm" className="text-neutral-600 font-bold">
              · Definition:
            </Text>
            <Text variant="body-sm" className="text-neutral-600">
              {device.definition}
            </Text>
          </FlexCol>
        )}
        {device.unclassifiedReasonCode && (
          <FlexRow gap="1" className="col-span-2">
            <Text variant="body-sm" className="text-neutral-600 font-bold">
              · Unclassified Reason:
            </Text>
            <Text variant="body-sm" className="text-neutral-600">
              {device.unclassifiedReasonCode}
            </Text>
          </FlexRow>
        )}
      </Box>
    </FlexCol>
  );
};
